import { Badge as BadgePrimitive } from "./primitives";
import { cn } from "./cn";

/**
 * Which system owns this field — Tally or the portal (`010` R5, FR-016).
 *
 * §14.4 splits customer fields between the two, and calls out that this is "not last-write-wins":
 * a Tally-side ledger name change must not overwrite the portal's tier or email.
 *
 * ## It sits on the field, not in a legend
 *
 * The failure mode is specific and slow: an admin corrects a legal name, the next scheduled pull
 * reverts it, and nobody connects the two. Weeks later the system is "unreliable". A note at the
 * top of the form is read once and then never again; the admin needs to know Tally owns this field
 * **at the moment they focus the input**. So this renders inline, beside the label, on every field
 * it applies to — which looks repetitive and is the point.
 *
 * Pass `id` and wire it into the input's `aria-describedby`, so a screen reader hears the ownership
 * with the field rather than as a stray badge after it.
 *
 * Ported from `packages/ui/src/field-owner-badge.tsx` (`017` left it out because `apps/admin` did
 * not import it yet — it does now, T022). `outline` here has no dedicated "warning" tone in the
 * template's token set, so the Tally-owned state borrows the same `orange` tokens `badge.tsx` uses
 * for `stale` — same amber-for-caution meaning, this package's actual vocabulary.
 */
export function FieldOwnerBadge({
  owner,
  id,
  className,
}: {
  owner: "tally" | "portal";
  id?: string;
  className?: string;
}) {
  const tally = owner === "tally";

  return (
    <BadgePrimitive
      variant="outline"
      id={id}
      data-slot="field-owner-badge"
      data-owner={owner}
      className={cn(
        "px-1.5 py-0 text-[0.6875rem] font-medium",
        tally ? "border-orange/40 bg-orange/10 text-orange-dark" : "border-muted bg-muted text-gray-700",
        className,
      )}
    >
      {tally ? "Tally owns this — edits here are overwritten on the next pull" : "The portal owns this"}
    </BadgePrimitive>
  );
}
